/**
 * features/media/MediaManager.tsx
 * ------------------------------------------------------------------
 * Website images — the admin library behind the public company page.
 *
 * One card per slot. Drop a screenshot on it (or click to browse) and
 * it is cropped to that slot's exact size, encoded, uploaded to
 * Storage and published live — no rebuild, no deploy.
 *
 * The slot list is derived from the landing page's PROJECTS array, so
 * adding work to the profile makes its upload card appear here on its
 * own.
 * ------------------------------------------------------------------
 */
import { useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Check,
  ImageIcon,
  Loader2,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Badge, Button, Card, PageHeader, Spinner } from '@/components/ui/primitives';
import { formatBytes } from '@/lib/imageResize';
import { isAdmin } from '@/lib/permissions';
import type { SiteImage } from '@/types';
import { useSiteImages } from '@/features/media/useSiteImages';
import { removeSlotImage, uploadSlotImage } from '@/features/media/siteImagesWrite';
import { ALL_SLOTS, SLOT_GROUPS, type ImageSlot } from '@/features/media/slots';

function SlotCard({
  slot,
  image,
  onChanged,
}: {
  slot: ImageSlot;
  image?: SiteImage;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const actor = { uid: user?.uid ?? '', email: user?.email ?? '' };

  async function accept(file: File | undefined) {
    if (!file) return;
    setError('');
    setDone(false);
    setBusy(true);
    try {
      await uploadSlotImage(slot, file, actor);
      setDone(true);
      window.setTimeout(() => setDone(false), 2200);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove() {
    if (!window.confirm(`Remove the image for “${slot.label}”? The section falls back to its placeholder.`)) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      await removeSlotImage(slot.id, actor);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the image.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-primary dark:text-gray-100">
            {slot.label}
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{slot.hint}</p>
        </div>
        {image ? <Badge tone="success">Live</Badge> : <Badge>Placeholder</Badge>}
      </div>

      {/* Drop zone / preview */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!busy) inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void accept(e.dataTransfer.files?.[0]);
        }}
        className={`relative mt-4 cursor-pointer overflow-hidden rounded-xl border-2 border-dashed transition ${
          dragging
            ? 'border-brand bg-brandSoft'
            : 'border-gray-200 bg-gray-50 hover:border-brand/50 dark:border-white/10 dark:bg-white/5'
        }`}
        style={{ aspectRatio: `${slot.width} / ${slot.height}` }}
      >
        {image && !busy && (
          <img
            src={image.url}
            alt={slot.label}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        {(!image || busy) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
            {busy ? (
              <>
                <Loader2 size={22} className="animate-spin text-brand" />
                <span className="text-xs font-semibold text-primaryLight dark:text-gray-300">
                  Resizing &amp; uploading…
                </span>
              </>
            ) : (
              <>
                <ImageIcon size={24} className="text-gray-400" />
                <span className="text-xs font-semibold text-primaryLight dark:text-gray-300">
                  Drop a screenshot, or click to choose
                </span>
                <span className="text-[11px] text-gray-400">
                  Any size — cropped to {slot.width}×{slot.height}
                </span>
              </>
            )}
          </div>
        )}

        {done && (
          <div className="absolute right-2 top-2 flex items-center gap-1.5 rounded-full bg-green-600 px-2.5 py-1 text-[11px] font-semibold text-white">
            <Check size={12} /> Published
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/*"
        className="hidden"
        onChange={(e) => void accept(e.target.files?.[0])}
      />

      {error && (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
          <AlertCircle size={13} className="mt-0.5 flex-none" />
          {error}
        </p>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <span className="truncate text-[11px] text-gray-400">
          {image
            ? `${image.width}×${image.height} · ${formatBytes(image.bytes)} · ${new Date(
                image.updatedAt,
              ).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' })}`
            : `Target ${slot.width}×${slot.height}`}
        </span>
        <div className="flex flex-none gap-1.5">
          <Button
            variant="ghost"
            className="!px-2.5 !py-1.5"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            icon={image ? <RefreshCw size={14} /> : <Upload size={14} />}
          >
            {image ? 'Replace' : 'Upload'}
          </Button>
          {image && (
            <Button
              variant="ghost"
              className="!px-2.5 !py-1.5 !text-red-600 dark:!text-red-400"
              onClick={() => void remove()}
              disabled={busy}
              icon={<Trash2 size={14} />}
            >
              Remove
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export function MediaManager() {
  const { role } = useAuth();
  const { images, loading } = useSiteImages();
  const [, setNonce] = useState(0);

  const counts = useMemo(() => {
    const filled = ALL_SLOTS.filter((s) => images[s.id]).length;
    return { filled, total: ALL_SLOTS.length };
  }, [images]);

  if (!isAdmin(role)) {
    return (
      <Card>
        <p className="text-sm text-primaryLight dark:text-gray-400">
          Website images can only be managed by a system admin.
        </p>
      </Card>
    );
  }

  if (loading) return <Spinner label="Loading website images…" />;

  return (
    <div>
      <PageHeader
        title="Website images"
        subtitle="Screenshots shown on the public company page. Uploads are cropped to size and go live immediately — no deploy needed."
        actions={
          <Badge tone={counts.filled === counts.total ? 'success' : 'warning'}>
            {counts.filled} of {counts.total} uploaded
          </Badge>
        }
      />

      {SLOT_GROUPS.map((group) => {
        const slots = ALL_SLOTS.filter((s) => s.group === group);
        return (
          <section key={group} className="mb-10">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
              {group}
            </h2>
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {slots.map((slot) => (
                <SlotCard
                  key={slot.id}
                  slot={slot}
                  image={images[slot.id]}
                  onChanged={() => setNonce((n) => n + 1)}
                />
              ))}
            </div>
          </section>
        );
      })}

      <Card className="bg-brandSoft/60">
        <h3 className="text-sm font-semibold text-primary dark:text-gray-100">
          Getting good screenshots
        </h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-primaryLight dark:text-gray-400">
          <li>
            Capture the browser at a wide window — a full-page screenshot of the
            landing page or main dashboard reads best.
          </li>
          <li>
            Tall screenshots are cropped from the <strong>top</strong>, so lead with the
            part you want seen. Wide ones are cropped evenly from both sides.
          </li>
          <li>Use demo or anonymised data — these are public.</li>
          <li>
            New work added to the company page appears here as an empty slot
            automatically.
          </li>
        </ul>
      </Card>
    </div>
  );
}
